import { useState, useEffect, useMemo } from 'react';
import { useAuth, useDemoFriends } from '../hooks/useAuth';
import { strings } from '../strings';
import {
  CalendarGrid,
  CalendarSlot,
  CalendarWeek,
  HoverCardData,
} from '../components/CalendarGrid';
import { TonightCard, TonightFriend } from '../components/TonightCard';
import { FriendsPanel, Friend } from '../components/FriendsPanel';
import { Header } from '../components/Header';
import { AccountMenu } from '../components/AccountMenu';
// import { SharingOffState } from '../components/SharingOffState'; // TODO: Use when implementing sharing off state
import { generateAndCopyInvite, generateAndCopyInviteForFriend } from '../utils/invite';
import { showToast } from '../utils/toast';
import {
  computeAvailability,
  UserAvailability,
  Rule,
  Exception,
  utcToLocal,
  SLOT_MINUTES,
} from '../engine/timeEngine';

const MOCK_PATTERN = [
  [0, 0, 0, 0, 0, 1, 1],
  [0, 0, 0, 0, 1, 1, 1],
  [0, 1, 1, 0, 1, 2, 2],
  [0, 1, 1, 1, 1, 2, 2],
  [1, 1, 2, 1, 2, 3, 3],
  [1, 2, 2, 1, 2, 3, 3],
  [2, 3, 3, 2, 4, 3, 4],
  [2, 3, 3, 2, 4, 4, 4],
  [2, 4, 3, 2, 5, 4, 4],
  [2, 4, 3, 2, 5, 4, 4],
  [1, 3, 2, 1, 5, 4, 3],
  [1, 3, 2, 1, 5, 3, 3],
  [0, 1, 1, 0, 3, 3, 1],
  [0, 1, 1, 0, 3, 2, 1],
];

function buildMockWeek(offset: number): CalendarWeek {
  const today = new Date();
  const currentDay = today.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() + mondayOffset + offset * 7);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const slots: CalendarSlot[] = [];
  MOCK_PATTERN.forEach((dayCounts, slotIdx) => {
    const hour = 17 + Math.floor(slotIdx / 2);
    const minute = (slotIdx % 2) * 30;
    const hourMinute = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    dayCounts.forEach((count, dayIdx) => {
      const slotDate = new Date(weekStart);
      slotDate.setDate(weekStart.getDate() + dayIdx);
      slotDate.setHours(hour, minute, 0, 0);
      slots.push({ timestamp: slotDate.getTime(), count, dayIndex: dayIdx, hourMinute });
    });
  });
  return { weekStart, weekEnd, slots };
}

function buildMockTonight(): {
  friends: TonightFriend[];
  bestTimeSlot: { day: string; timeRange: string; count: number; totalSharing: number } | null;
} {
  const now = new Date();
  const currentHour = now.getHours();
  const tonightFriends: TonightFriend[] = [];
  if (currentHour >= 19 && currentHour < 24) {
    tonightFriends.push({
      name: 'Mara',
      initials: 'MA',
      localTime: '21:00',
      isAvailableNow: currentHour >= 21,
    });
    tonightFriends.push({
      name: 'Joeri',
      initials: 'JO',
      localTime: '21:00',
      isAvailableNow: currentHour >= 21,
    });
    if (currentHour >= 20) {
      tonightFriends.push({
        name: 'Priya',
        initials: 'PR',
        localTime: '20:00',
        timezone: 'London',
        isAvailableNow: currentHour >= 21,
      });
    }
  }
  const bestTimeSlot = { day: 'Fri', timeRange: '21:00 – 23:00', count: 5, totalSharing: 5 };
  return { friends: tonightFriends, bestTimeSlot };
}

function buildMockFriends(
  demoFriends: ReturnType<typeof useDemoFriends>,
  friendsVisibility: Record<string, boolean>
): Friend[] {
  return demoFriends.map((df) => ({
    id: df.discord_id,
    displayName: df.display_name,
    initials: df.display_name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase(),
    timezone: df.timezone,
    nextFreeTime: 'tonight 20:00',
    isSharing: df.sharing,
    isOnSquadTime: true,
    showInCalendar: friendsVisibility[df.discord_id] !== false,
  }));
}

export function MainPage() {
  const { user, demoMode } = useAuth();
  const demoFriends = useDemoFriends();

  const [weekOffset, setWeekOffset] = useState(0);
  const [hoverCard, setHoverCard] = useState<HoverCardData | null>(null);
  const [friendsVisibility, setFriendsVisibility] = useState<Record<string, boolean>>({});
  const [realFriends, setRealFriends] = useState<Friend[]>([]);
  const [realRules, setRealRules] = useState<Rule[]>([]);
  const [realExceptions, setRealExceptions] = useState<Exception[]>([]);
  const [friendUserData, setFriendUserData] = useState<
    Map<string, { rules: Rule[]; exceptions: Exception[] }>
  >(new Map());

  // Load real friends when logged in with Discord
  useEffect(() => {
    if (demoMode || !user) return;
    let cancelled = false;
    fetch('/api/friends', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { friends: [] }))
      .then((data) => {
        const list = (data.friends || []).map(
          (f: {
            discord_id: string;
            display_name: string;
            timezone: string;
            sharing: boolean;
            show_in_calendar?: boolean;
          }) => ({
            id: f.discord_id,
            displayName: f.display_name,
            initials: f.display_name.slice(0, 2).toUpperCase(),
            timezone: f.timezone,
            isSharing: f.sharing === true,
            isOnSquadTime: true,
            showInCalendar:
              friendsVisibility[f.discord_id] !== undefined
                ? friendsVisibility[f.discord_id]
                : f.show_in_calendar !== false,
          })
        ) as Friend[];
        if (!cancelled) setRealFriends(list);
      })
      .catch(() => {
        if (!cancelled) setRealFriends([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, demoMode]);

  useEffect(() => {
    if (demoMode || !user) return;
    Promise.all([
      fetch('/api/rules', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : { rules: [] }
      ),
      fetch('/api/exceptions', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : { exceptions: [] }
      ),
    ])
      .then(([rulesData, excData]) => {
        const rules = (rulesData.rules || []).map(
          (r: {
            id: number;
            user_id: string;
            weekdays: string | number[];
            start_time: string;
            end_time: string;
            tz: string;
            valid_from: number;
            valid_to: number | null;
          }) => ({
            id: r.id,
            user_id: r.user_id,
            weekdays:
              typeof r.weekdays === 'string' ? r.weekdays.split(',').map(Number) : r.weekdays,
            start_time: r.start_time,
            end_time: r.end_time,
            tz: r.tz,
            valid_from: r.valid_from,
            valid_to: r.valid_to,
          })
        ) as Rule[];
        const exceptions = (excData.exceptions || []).map(
          (e: {
            id: number;
            user_id: string;
            rule_id: number | null;
            local_date: string;
            type: 'cancelled' | 'changed' | 'extra';
            start_time: string | null;
            end_time: string | null;
          }) => ({
            id: e.id,
            user_id: e.user_id,
            rule_id: e.rule_id,
            local_date: e.local_date,
            type: e.type,
            start_time: e.start_time,
            end_time: e.end_time,
          })
        ) as Exception[];
        setRealRules(rules);
        setRealExceptions(exceptions);
      })
      .catch(() => {
        setRealRules([]);
        setRealExceptions([]);
      });
  }, [user, demoMode]);

  // Load each sharing friend's rules/exceptions so they appear on the calendar
  useEffect(() => {
    if (demoMode || !user) return;
    let cancelled = false;
    const sharingFriends = realFriends.filter((f) => f.isSharing && f.showInCalendar !== false);
    const mapRule = (r: {
      id: number;
      user_id: string;
      weekdays: string | number[];
      start_time: string;
      end_time: string;
      tz: string;
      valid_from: number;
      valid_to: number | null;
    }): Rule => ({
      id: r.id,
      user_id: r.user_id,
      weekdays: typeof r.weekdays === 'string' ? r.weekdays.split(',').map(Number) : r.weekdays,
      start_time: r.start_time,
      end_time: r.end_time,
      tz: r.tz,
      valid_from: r.valid_from,
      valid_to: r.valid_to,
    });
    const mapExc = (e: {
      id: number;
      user_id: string;
      rule_id: number | null;
      local_date: string;
      type: 'cancelled' | 'changed' | 'extra';
      start_time: string | null;
      end_time: string | null;
    }): Exception => ({
      id: e.id,
      user_id: e.user_id,
      rule_id: e.rule_id,
      local_date: e.local_date,
      type: e.type,
      start_time: e.start_time,
      end_time: e.end_time,
    });
    Promise.allSettled(
      sharingFriends.map(async (f) => {
        const [rulesData, excData] = await Promise.all([
          fetch(`/api/rules?user_id=${encodeURIComponent(f.id)}`, { credentials: 'include' }).then(
            (r) => (r.ok ? r.json() : { rules: [] })
          ),
          fetch(`/api/exceptions?user_id=${encodeURIComponent(f.id)}`, {
            credentials: 'include',
          }).then((r) => (r.ok ? r.json() : { exceptions: [] })),
        ]);
        return {
          id: f.id,
          rules: (rulesData.rules || []).map(mapRule),
          exceptions: (excData.exceptions || []).map(mapExc),
        };
      })
    )
      .then((results) => {
        if (cancelled) return;
        const next = new Map<string, { rules: Rule[]; exceptions: Exception[] }>();
        for (const r of results) {
          if (r.status === 'fulfilled') next.set(r.value.id, r.value);
        }
        setFriendUserData(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, demoMode, realFriends]);

  const hasFriends = (demoMode && demoFriends.length > 0) || realFriends.length > 0;
  // const isSharing = user?.sharing !== false; // TODO: Use for sharing off state

  const handleSlotClick = (slot: CalendarSlot) => {
    console.log('Slot clicked:', slot);
  };

  const friends = demoMode
    ? buildMockFriends(demoFriends, friendsVisibility)
    : realFriends.map((f) => ({
        ...f,
        showInCalendar:
          friendsVisibility[f.id] !== undefined ? friendsVisibility[f.id] : f.showInCalendar,
      }));

  const getHoverCard = (slot: CalendarSlot): HoverCardData | null => {
    if (demoMode) {
      const mockFriends = [
        { name: 'Priya', initials: 'PR', localTime: '19:00', timezone: 'London' },
        { name: 'Mara', initials: 'MA', localTime: '20:00' },
      ].slice(0, slot.count);
      const notFree = ['Joeri', 'Sanne', 'Tom'].slice(0, Math.max(0, 5 - slot.count));
      return { slot, friends: mockFriends, notFreeFriends: notFree };
    }
    if (!weekAvailResult || !user) return null;
    const slotEnd = slot.timestamp + SLOT_MINUTES * 60 * 1000;
    const free: { name: string; initials: string; localTime: string; timezone?: string }[] = [];
    const notFree: string[] = [];
    const allUsers = [
      { id: user.discord_id, name: user.display_name, tz: user.timezone || 'Europe/Amsterdam' },
      ...realFriends
        .filter((f) => f.isSharing && f.showInCalendar !== false)
        .map((f) => ({ id: f.id, name: f.displayName, tz: f.timezone })),
    ];
    for (const u of allUsers) {
      const windows = weekAvailResult.userWindows.get(u.id) || [];
      const overlapping = windows.some((w) => w.start < slotEnd && w.end > slot.timestamp);
      if (overlapping) {
        const local = utcToLocal(slot.timestamp, u.tz);
        free.push({
          name: u.name,
          initials: u.name.slice(0, 2).toUpperCase(),
          localTime: local.time,
          timezone: u.tz.split('/').pop(),
        });
      } else {
        notFree.push(u.name);
      }
    }
    return { slot, friends: free, notFreeFriends: notFree };
  };

  const handleSlotHover = (slot: CalendarSlot | null) => {
    setHoverCard(slot ? getHoverCard(slot) : null);
  };

  const handleToggleFriend = async (friendId: string, show: boolean) => {
    setFriendsVisibility((prev) => ({ ...prev, [friendId]: show }));
    setRealFriends((prev) =>
      prev.map((f) => (f.id === friendId ? { ...f, showInCalendar: show } : f))
    );
    if (demoMode) return;
    try {
      await fetch(`/api/friends/${friendId}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ show_in_calendar: show }),
      });
    } catch {
      setFriendsVisibility((prev) => ({ ...prev, [friendId]: !show }));
    }
  };

  const handleInviteFriend = async (friendId: string) => {
    const friend = friends.find((f: Friend) => f.id === friendId);
    if (!friend) return;

    const success = await generateAndCopyInviteForFriend(friend.displayName);
    if (success) {
      showToast({ message: strings.invite.copied, type: 'success' });
    } else {
      showToast({ message: 'Failed to copy invite', type: 'error' });
    }
  };

  const handleInviteNew = async () => {
    const success = await generateAndCopyInvite();
    if (success) {
      showToast({ message: strings.invite.copied, type: 'success' });
    } else {
      showToast({ message: 'Failed to copy invite', type: 'error' });
    }
  };

  const computeWeekRange = (offset: number) => {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + mondayOffset + offset * 7);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return { weekStart, weekEnd };
  };

  const weekAvailResult = useMemo(() => {
    if (demoMode) return null;
    const { weekStart, weekEnd } = computeWeekRange(weekOffset);
    const tz = user?.timezone || 'Europe/Amsterdam';
    const users: UserAvailability[] = [
      { user_id: user?.discord_id || 'me', rules: realRules, exceptions: realExceptions },
    ];
    for (const f of realFriends) {
      if (f.isSharing && f.showInCalendar !== false) {
        const fd = friendUserData.get(f.id);
        users.push({ user_id: f.id, rules: fd?.rules ?? [], exceptions: fd?.exceptions ?? [] });
      }
    }
    return computeAvailability(users, tz, weekStart.getTime(), weekEnd.getTime());
  }, [demoMode, weekOffset, user, realRules, realExceptions, realFriends, friendUserData]);

  const weekData: CalendarWeek = useMemo(() => {
    if (demoMode) return buildMockWeek(weekOffset);
    const { weekStart, weekEnd } = computeWeekRange(weekOffset);
    const tz = user?.timezone || 'Europe/Amsterdam';
    const avail =
      weekAvailResult ||
      computeAvailability(
        [{ user_id: user?.discord_id || 'me', rules: realRules, exceptions: realExceptions }],
        tz,
        weekStart.getTime(),
        weekEnd.getTime()
      );
    const slots: CalendarSlot[] = [];
    const slotFreeFriends = new Map<number, string[]>();
    for (const [ts, count] of avail.slotCounts.entries()) {
      const slotEnd = ts + SLOT_MINUTES * 60 * 1000;
      const dayIndex = Math.floor((ts - weekStart.getTime()) / (24 * 60 * 60 * 1000));
      const local = utcToLocal(ts, tz);
      const free: string[] = [];
      for (const f of realFriends) {
        if (!f.isSharing || f.showInCalendar === false) continue;
        const windows = avail.userWindows.get(f.id) || [];
        if (windows.some((w) => w.start < slotEnd && w.end > ts)) {
          free.push(f.displayName);
        }
      }
      slotFreeFriends.set(ts, free);
      slots.push({ timestamp: ts, count, dayIndex, hourMinute: local.time, freeFriends: free });
    }
    return { weekStart, weekEnd, slots, slotFreeFriends };
  }, [demoMode, weekOffset, user, realRules, realExceptions, realFriends, weekAvailResult]);

  const { friends: tonightFriendsComputed, bestTimeSlot } = useMemo(() => {
    if (demoMode) return buildMockTonight();
    if (!user) return { friends: [] as TonightFriend[], bestTimeSlot: null };
    const tz = user.timezone || 'Europe/Amsterdam';
    const avail = computeAvailability(
      [{ user_id: user.discord_id, rules: realRules, exceptions: realExceptions }],
      tz,
      Date.now(),
      Date.now() + 3 * 60 * 60 * 1000
    );
    const tonight = avail.tonight;
    const friendsList: TonightFriend[] = tonight.map((w) => {
      const local = utcToLocal(w.start, tz);
      return {
        name: user.display_name,
        initials: user.display_name.slice(0, 2).toUpperCase(),
        localTime: local.time,
        isAvailableNow: w.start <= Date.now(),
      };
    });
    const best =
      tonight.length > 0
        ? {
            day: new Date(tonight[0].start).toLocaleDateString('en-US', { weekday: 'short' }),
            timeRange: `${utcToLocal(tonight[0].start, tz).time} – ${utcToLocal(tonight[0].end, tz).time}`,
            count: tonight.length,
            totalSharing: tonight.length,
          }
        : null;
    return { friends: friendsList, bestTimeSlot: best };
  }, [demoMode, user, realRules, realExceptions]);

  const weekDataResolved = weekData;
  const tonightFriends = demoMode ? buildMockTonight().friends : tonightFriendsComputed;
  const tonightBest = demoMode ? buildMockTonight().bestTimeSlot : bestTimeSlot;

  return (
    <div style={styles.page}>
      <Header right={<AccountMenu />} />

      <main style={styles.main}>
        <div style={styles.layout}>
          {/* Friends panel - left side on desktop */}
          {hasFriends && (
            <FriendsPanel
              friends={friends}
              onToggleShowInCalendar={handleToggleFriend}
              onInviteFriend={handleInviteFriend}
              onInviteNew={handleInviteNew}
            />
          )}

          {/* Main content area */}
          <section style={styles.mainContent}>
            {/* Hero section with title and description */}
            <div style={styles.hero}>
              <div>
                <h2 style={styles.heroTitle}>
                  {strings.home.title.split('game').map((part, i, arr) => (
                    <span key={i}>
                      {part}
                      {i < arr.length - 1 && <span style={{ color: 'var(--mint)' }}>game</span>}
                    </span>
                  ))}
                </h2>
                <p style={styles.heroDescription}>
                  Sharing goes both ways: if you stop sharing your times, you won't see your
                  friends' times either. Manage this in Settings.
                </p>
              </div>
              <a href="/add-times" style={styles.addTimesButton}>
                {strings.home.primaryAction}
              </a>
            </div>

            {hasFriends ? (
              <>
                {/* Tonight card */}
                {(tonightFriends.length > 0 || tonightBest) && (
                  <TonightCard
                    friends={tonightFriends}
                    bestTimeSlot={tonightBest}
                    onSeeThisEvening={() => console.log('See this evening')}
                  />
                )}

                {/* Calendar */}
                <CalendarGrid
                  weekData={weekDataResolved}
                  timezone={user?.timezone || 'Europe/Amsterdam'}
                  onPrevWeek={() => {
                    if (weekOffset > 0) setWeekOffset(weekOffset - 1);
                  }}
                  onNextWeek={() => setWeekOffset(weekOffset + 1)}
                  isCurrentWeek={weekOffset === 0}
                  onSlotClick={handleSlotClick}
                  onSlotHover={handleSlotHover}
                  hoverCard={hoverCard}
                />

                {/* Coming soon section */}
                <div style={styles.comingSoonSection}>
                  <span style={styles.comingSoonLabel}>Coming soon</span>
                  <div style={styles.comingSoonPills}>
                    <span style={styles.comingSoonPill}>Pick a game</span>
                    <span style={styles.comingSoonPill}>Reminders</span>
                    <span style={styles.comingSoonPill}>Calendar feed</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Empty state when no friends */}
                <div style={styles.noFriendsCard}>
                  <h3 style={styles.noFriendsTitle}>Gaming is more fun with a squad</h3>
                  <p style={styles.noFriendsDescription}>
                    Invite your friends to see when everyone can play together across timezones.
                  </p>
                  <button onClick={handleInviteNew} style={styles.inviteButton}>
                    Invite friends
                  </button>
                  <a
                    href="https://discord.gg/XmzRNm9N6Y"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.communityLink}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.445.764-.693 1.163a19.277 19.277 0 0 0-5.425 0 12.62 12.62 0 0 0-.695-1.163.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.68 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.083.083 0 0 0 .031.057 19.9 19.9 0 0 0 5.994 6.267.077.077 0 0 0 .084-.028c.464-.613.977-1.217 1.483-1.847a.077.077 0 0 1 .085-.001 13.294 13.294 0 0 0 1.635 1.543.074.074 0 0 0 .084.028c2.651-1.636 5.128-3.595 6.967-5.071a.077.077 0 0 0 .083-.028.07.07 0 0 0 .031-.057c.423-4.627-.472-9.226-3.548-13.66a.061.061 0 0 0-.032-.027zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418z" />
                    </svg>
                    Join our Discord community
                  </a>
                </div>

                {/* Still show coming soon even without friends */}
                <div style={styles.comingSoonSection}>
                  <span style={styles.comingSoonLabel}>Coming soon</span>
                  <div style={styles.comingSoonPills}>
                    <span style={styles.comingSoonPill}>Pick a game</span>
                    <span style={styles.comingSoonPill}>Reminders</span>
                    <span style={styles.comingSoonPill}>Calendar feed</span>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        {demoMode && (
          <section style={styles.debugSection}>
            <h3>Debug Info</h3>
            <p style={styles.debugNote}>
              Toggle demo mode off to see the empty state. Current user: {user?.display_name}
            </p>
          </section>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
  },
  logoutButton: {
    background: 'var(--surface)',
    border: '1px solid var(--border-strong)',
    color: 'var(--text)',
    padding: '8px 16px',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  demoButton: {
    background: 'var(--surface)',
    border: '1px solid var(--border-strong)',
    color: 'var(--text)',
    padding: '8px 16px',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  },
  main: {
    maxWidth: '1360px',
    margin: '0 auto',
    padding: '24px 32px',
  },
  layout: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '24px',
  },
  mainContent: {
    flex: '3 1 560px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  hero: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  heroTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  heroSubtitle: {
    fontSize: '14px',
    color: 'var(--muted)',
    margin: '6px 0 0',
  },
  heroDescription: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: 'var(--text-2)',
    margin: '8px 0 0',
    maxWidth: '560px',
  },
  addTimesButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
    padding: '0 24px',
    background: 'var(--mint)',
    color: '#0A0E0D',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  noFriendsCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  noFriendsTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  noFriendsDescription: {
    fontSize: 14,
    lineHeight: 1.5,
    color: 'var(--text-2)',
    margin: 0,
    maxWidth: '400px',
  },
  communityLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: 'var(--mint)',
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    marginTop: 4,
  },
  inviteButton: {
    minHeight: '48px',
    padding: '0 24px',
    background: 'var(--mint)',
    color: '#0A0E0D',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  comingSoonSection: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
    background: 'var(--bg-bar)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
  },
  comingSoon: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--muted)',
  },
  comingSoonLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-2)',
  },
  comingSoonPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  comingSoonPill: {
    padding: '6px 12px',
    borderRadius: '999px',
    border: '1px dashed var(--border-strong)',
    fontSize: '13px',
    color: 'var(--muted)',
    background: 'var(--surface)',
  },
  debugSection: {
    marginTop: '48px',
    paddingTop: '24px',
    borderTop: '1px solid var(--border)',
  },
  debugNote: {
    fontSize: '14px',
    color: 'var(--text-2)',
  },
};
